import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { pick, toLower, has, filter, size } from "lodash";
import { Page404 } from "../../components/Page404";
import { Paragraph, Title } from "../../components/Text";
import { editValues, showForm } from "../../store/formSlice";
import { deleteShcFile, downloadPackage } from "../../store/uploadSlice";
import { IssueDetails } from "./IssueDetails";
import { OrgDetails } from "./OrgDetails";
import { PkgDetails } from "./pkg/PkgDetails";
import { UserDetails } from "./UserDetails";
import { pingServer } from "../../store/userSlice";
import { ContractDetails } from "./ContractDetails";
import { AuditDetails } from "./AuditDetails";
import { MasterKitList } from "./pkg/MasterKitList";
import { Page403 } from "../../components/Page403";
import { activateContract, deleteContract, deleteOrgContract } from "../../store/api/contracts";
import { getDetails, setErrors } from "../../store/dataSlice";
import { addAttachment, addIssueComment, deleteAttachment, downloadAttachment, getIssueAttachments, updateAttachment } from "../../store/api/issues";
import { addPackageDocumetationLink, archivePackageVersion, deletePackage, deletePackageDocumetationLink, downloadMasterKitList, editPackageDocumetationLink, getMasterKitList, getPackagesByIds, verifyPackage } from "../../store/api/packages";
import { changeUserAccess, deleteUser, revokeGrant } from "../../store/api/users";
import { Modal } from "../../components/Modal";
import { getAuditPackageDetails, getFullPackageAudit } from "../../store/api/audit";
import { LicenseDetails } from "./LicenseDetails";
import { deleteOrg, deleteOrgLicense, downloadOrgLicense } from "../../store/api/organisations";
import { UploadDetails } from "./UploadDetails";
import { PkgMassUpdate } from "./pkg/PkgMassUpdate";
import { setNotification } from "../../store/notificationsSlice";
import { renderFieldContent } from "../../components/Layout";


const DetailsContainer = ({ match, location, history }) => {
    const { pathname, search } = location;
    const { org, tab, selected } = match.params;
    const urlParts = pathname.split("/"), page = urlParts[1];
    const dispatch = useDispatch();
    const { loading, details, notFound, forbidden, shouldUpdate, errors } = useSelector(state => state.data);
    const { visible, hasChanges } = useSelector(state => state.form);
    const { uploads } = useSelector(state => state.upload);
    const { grants } = useSelector(state => state.user);
    let urlParams = new URLSearchParams(search);
    let ids = urlParams.get("ids");

    const updateDetails = () => {
        if (page === "audit") {
            if (tab === "packages") {
                return dispatch(getAuditPackageDetails({ auditNumber: selected }));
            } else {
                return dispatch(getDetails({ page: page, tab: tab, selected: selected }));
            }
        } else if (page == "packages" && selected == "edit") {
            return dispatch(getPackagesByIds(ids));
        } else if (page == "packages" && selected == "masterkit") {
            return dispatch(getMasterKitList({ params: { swtype: "e,l,o" }}));
        } else if (tab) {
            return dispatch(getDetails({ page: tab, selected: selected }));
        } else if (org) {
            return dispatch(getDetails({ page: page, org: org, selected: org }));
        };
    };

    useEffect(() => {
        updateDetails();
    }, [ org, tab, selected, search ]);

    useEffect(() => {
        if (urlParts.includes(shouldUpdate) || (page == "organisations" && shouldUpdate == "contracts")) {
            updateDetails();
        }
    }, [ shouldUpdate ]);

    const checkChanges = (key, values) => {
        if (key !== visible) {
            if (hasChanges) {
                Modal.confirm({
                    centered: true,
                    icon: null,
                    className: "sp-confirm-modal",
                    title: <Title h4 color="#E8AF1D">Warning</Title>,
                    content: <Paragraph>Changes will not be saved. Are you sure you want to close the form?</Paragraph>,
                    okText: "Close",
                    okButtonProps: {style: { height: "40px", padding: "0px 21px", background: "var(--sp-main-color)", borderRadius: "8px", border: "none" }},
                    cancelButtonProps: {type: "link", style:{ fontSize: "14px", color: "var(--sp-main-color)" }},
                    onOk() {
                        onFormShow(key, values);
                    },
                    onCancel() {}
                });
            } else {
                onFormShow(key, values);
            }
        };
    };

    const onFormShow = (key, values) => {
        if (values) {
            dispatch(editValues({ entity: key, values: values }));
        } else {
            dispatch(showForm(key));
        };
    };

    const closeDeleted = (id) => {
        if (pathname.split("/").pop() == id) {
            onClose();
        };
    };

    const revokeGrantConfirm = ({ email, grantName, orgAlias, orgName }) => {
        Modal.confirm({
            okText: "Ok",
            content: <Paragraph>Are you sure you want to revoke the <b>{grantName}</b> grant issued for <b>{orgName}</b>?</Paragraph>,
            onOk: () => dispatch(revokeGrant({ email: email, alias: orgAlias }))
                .unwrap()
                .then(() => grants.isAdmin ? updateDetails() : closeDeleted(email))
                .catch(() => {})
        })
    };

    const confirmDisablingUser = (email) => {
        Modal.confirm({
            okText: "Ok",
            content: <Paragraph>Are you sure you want to disable access for <b>{email}</b>?</Paragraph>,
            onOk: () => dispatch(changeUserAccess({ email: email, disable: false }))
                .unwrap()
                .then(() => updateDetails())
                .catch(() => {})
        });
    };

    const confirmRemovingDocLink = ({ id, description, packageNumber }) => {
        Modal.confirm({
            okText: "Ok",
            cancelText: "Cancel",
            content: <Paragraph>Are you sure you want to delete the <b>{description}</b> link?</Paragraph>,
            onOk: () => dispatch(deletePackageDocumetationLink({ id, packageNumber }))
        });
    };

    const confirmRemovingAttachment = ({content, id}) => {
        Modal.confirm({
            content: content,
            onOk: () => dispatch(deleteAttachment({ issue: selected, id: id }))
                .unwrap()
                .then(() => dispatch(getIssueAttachments(selected)))
                .catch(() => {})
        });
    };

    const confirmRemovingContract = ({ alias, contract, org }) => {
        Modal.confirm({
            content: <Paragraph>Are you sure you want to delete the <b>{contract}</b> contract for <b>{org}</b>?</Paragraph>,
            onOk: () => dispatch(deleteOrgContract({ alias: alias, contract: toLower(contract) })).then(() => updateDetails())
        });
    };

    const confirmRemovingItem = ({ content, id }) => {
        const fs = {
            organisations: deleteOrg,
            licenses: deleteOrgLicense,
            shc: deleteShcFile,
            packages: deletePackage,
            users: deleteUser,
            contracts: deleteContract
        };
        Modal.confirm({
            content: content,
            onOk: () => Object.keys(fs).includes(tab || page)
                ? dispatch(fs[tab || page](id))
                    .unwrap()
                    .then(() => closeDeleted(id))
                    .catch(() => {})
                : null
            });
    };

    const confirmRemovingPkg = ({ key, name }) => {
        Modal.confirm({
            content: <Paragraph>Are you sure you want to remove the <b>{name}</b> package from the list?</Paragraph>,
            onOk: () => {
                let idsArray = ids.split(",");
                let filteredIds = filter(idsArray, id => id != key).join(",");
                if (filteredIds.length) {
                    urlParams.set("ids", filteredIds);
                    history.replace({ search: urlParams.toString() });
                } else {
                    onClose();
                }
            }
        })
    };

    const updatePackages = ({ ids=[], action, ...values }) => {
        const actions = { verify: verifyPackage, archive: archivePackageVersion };
        if (Object.keys(actions).includes(action)) {
            Promise.all(ids.map(id => dispatch(actions[action]({ id: Number(id), ...values }))))
            .then((res) => {
                let errors = {};
                filter(Object.values(res), o => o.error).map(err => {
                    errors[err.meta.arg.id] = err.payload;
                })
                updateDetails();
                if (size(errors)) {
                    dispatch(setNotification({ success: false, message: "Some of packages cannot be updated" }));
                    dispatch(setErrors(errors));
                }
            });
        }
    };

    const onClose = (e) => {
        if (e) e.preventDefault();
        if (location.state && has(location.state, "from")) {
            history.push(location.state.from);
        } else {
            history.push(pathname.substring(0, pathname.lastIndexOf("/")));
        }
    };

    const detailsProps = {
        pathname: pathname,
        data: details,
        selected: selected,
        loading: loading,
        renderFieldContent: renderFieldContent,
        onClose: onClose,
        ...grants
    }

    const content = {
        organisations: (
            <OrgDetails
                {...detailsProps}
                showForm={checkChanges}
                deleteItem={confirmRemovingItem}
                editContracts={(values) => onFormShow("contracts", values)}
                deleteContract={confirmRemovingContract}
            />
        ),
        users: (
            <UserDetails
                {...detailsProps}
                issueGrant={grants.isAdmin ? () => dispatch(showForm("grants")) : null}
                editInfo = {onFormShow}
                revokeGrant = {revokeGrantConfirm}
                renderFieldContent={renderFieldContent}
                changeUserAccess={grants.isAdmin || grants.userWrite && details.orgAlias === grants.orgAlias ? (email, disabled) => disabled ? dispatch(changeUserAccess({ email: email, disabled: true })).then(() => updateDetails()) : confirmDisablingUser(email) : null}
                deleteUser={grants.userDelete ? confirmRemovingItem : null}
            />
        ),
        issues: (
            <IssueDetails
                {...detailsProps}
                addComment={values => dispatch(addIssueComment({ issue: selected, ...values }))}
                pingServer={() => dispatch(pingServer())}
                downloadAttachment={(id) => dispatch(downloadAttachment({ issue: selected, id: id }))}
                addAttachment={files => dispatch(addAttachment({ issue: selected, visible: true, files: files }))}
                deleteAttachment={confirmRemovingAttachment}
                updateAttachment={(id, visible) => dispatch(updateAttachment({ issue: selected, id: id, visible: visible }))}
            />
        ),
        packages: (
            selected == "edit"
            ? <PkgMassUpdate
                { ...detailsProps }
                showForm={(values) => onFormShow("pkg_mass_update", values)}
                updatePackages={updatePackages}
                removePackage={confirmRemovingPkg}
                search={search}
                errors={errors}
            />
            : selected == "masterkit"
            ? <MasterKitList
                data={details}
                onClose={onClose}
                pathname={pathname}
                location={location}
                history={history}
                isAdmin={grants.isAdmin}
                loading={loading}
                downloadMasterKitList={() => dispatch(downloadMasterKitList())}
            />
            : <PkgDetails
                {...detailsProps}
                uploadProgress={Array.isArray(details.files) ? pick(uploads, details.files.map(file => file.key)) : {}}
                editInfo={(entity, values) => onFormShow(entity, values)}
                verifyPackage={(values) => dispatch(verifyPackage(values))
                        .unwrap()
                        .then(res => {
                            dispatch(setNotification(res));
                            updateDetails();
                        })
                        .catch(err => dispatch(setNotification(err)))
                    }
                archivePackage={({ key }) => dispatch(archivePackageVersion({ id: key }))
                    .unwrap()
                    .then(res => {
                        dispatch(setNotification(res));
                        updateDetails();
                    })
                    .catch(err => dispatch(setNotification(err)))}
                removePackage={confirmRemovingItem}
                downloadPackage={(values) => dispatch(downloadPackage(values))}
                renderFieldContent={renderFieldContent}
                addDocLink={(values) => dispatch(addPackageDocumetationLink({ packageNumber: selected, data: values }))}
                editDocLink={(values) => dispatch(editPackageDocumetationLink(values))}
                removeDocLink={confirmRemovingDocLink}
            />
        ),
        contracts: (
            <ContractDetails
                {...detailsProps}
                activateContract={(values) => dispatch(activateContract(values))}
                deleteContract={confirmRemovingItem}
            />
        ),
        audit: (
            <AuditDetails
                {...detailsProps}
                tab={tab}
                getFullPackageAudit={(values) => dispatch(getFullPackageAudit(values))}
            />
        ),
        licenses: (
            <LicenseDetails
                {...detailsProps}
                downloadLicense={(id) => dispatch(downloadOrgLicense(id))}
                editLicense={(values) => dispatch(editValues({entity: "licenses", values: values}))}
                deleteLicense={confirmRemovingItem}
            />
        ),
        shc: (
            <UploadDetails
                {...detailsProps}
                tab={tab}
                removeUpload={confirmRemovingItem}
            />
        )
    };

    if (forbidden) {
        return <Page403 />
    };

    if (notFound || (!org && !Object.keys(content).includes(tab)) && (!Object.keys(content).includes(page))) {
        return <Page404 />
    };

    return page === "audit"
        ? content.audit
        : tab
            ? content[tab]
            : org ? content.organisations : null
};

export { DetailsContainer };